"use client";

import clsx from "clsx";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Pie} from "@/components/common/charts/Pie";
import {CallsByTypeStat} from "@/types/admin/dashboard";
import {PieDataType} from "@/utils/common/charts";
import {GetTheme} from "@/utils/common/general";
import {SmartT} from "@/utils/common/translations";
import {GeneratePrimaryColorPalette} from "@/utils/library/color";

interface CommunicationMetricsBreakdownProps {
   callsByType: CallsByTypeStat[] | null | undefined;
}

export default function CommunicationMetricsBreakdown({
   callsByType,
}: Readonly<CommunicationMetricsBreakdownProps>) {
   const {t} = useTranslation("stats");
   const {t: ct} = useTranslation("common");

   const [pieData, setPieData] = useState<PieDataType[]>([]);

   // Get current theme - consistent with how Bar/Pie chart components handle theming
   const theme = GetTheme();

   useEffect(() => {
      if (!callsByType || callsByType.length === 0) {
         setPieData([]);
         return;
      }

      const colors = GeneratePrimaryColorPalette(6, theme);
      const total = callsByType.reduce((acc, stat) => acc + (stat.count || 0), 0);

      const newPieData: PieDataType[] = callsByType
         .map((stat, index) => ({
            active: true,
            id: stat.category,
            label: SmartT(stat.category, ct),
            value: stat.count || 0,
            color: colors[index % colors.length],
            percent: total > 0 ? ((stat.count || 0) / total) * 100 : 0,
         }))
         .sort((a, b) => b.value - a.value);

      setPieData(newPieData);
   }, [callsByType, ct, theme]);

   const hasData = pieData.some((d) => d.value > 0);

   return (
      <div className={clsx("flex", "flex-col", "h-full")}>
         <h3 className={clsx("text-lg", "font-medium", "text-900", "mb-4")}>
            {t("communicationMetrics.callTypeBreakdown")}
         </h3>
         {hasData ? (
            <div className={clsx("flex", "flex-1", "min-h-[200px]", "gap-4")}>
               {/* Chart - overflow-hidden is critical for Nivo charts to shrink properly */}
               <div className={clsx("flex-1", "h-full", "min-h-[180px]", "overflow-hidden")}>
                  <Pie
                     data={pieData}
                     innerRadius={0.8}
                     themeOverride={theme}
                     enableArcLinkLabels={false}
                     enableArcLabels={false}
                     padAngle={0}
                  />
               </div>
               {/* Legend */}
               <div
                  className={clsx("flex", "flex-col", "justify-center", "gap-3", "min-w-[120px]")}
               >
                  {pieData.map((item) => (
                     <div key={item.id} className={clsx("flex", "items-center", "gap-2")}>
                        <div
                           className={clsx("w-3", "h-3", "rounded-full", "flex-shrink-0")}
                           style={{backgroundColor: item.color}}
                        />
                        <span className={clsx("text-sm", "text-700", "flex-1")}>{item.label}</span>
                        <span className={clsx("text-sm", "font-medium", "text-900")}>
                           {Math.round(item.percent || 0)}%
                        </span>
                     </div>
                  ))}
               </div>
            </div>
         ) : (
            <div
               className={clsx(
                  "flex-1",
                  "flex",
                  "items-center",
                  "justify-center",
                  "text-500",
                  "min-h-[200px]"
               )}
            >
               {t("noStats")}
            </div>
         )}
      </div>
   );
}
